import { useEffect, useRef, useState } from "react";

import { AlertTriangle, CornerDownLeft, Loader2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownView } from "@/lib/markdown";
import { cn } from "@/lib/utils";

import type { KbRagSearch } from "..";
import {
  useKbAsk,
  useKbModels,
  type ChatMessage,
  type SearchHit,
} from "../api";
import { ArticleHit } from "./ArticleHit";
import { CategorySelect } from "./CategorySelect";

interface Turn extends ChatMessage {
  citations?: SearchHit[];
  model?: string;
}

export function AskTab({
  search,
  setSearch,
}: {
  search: KbRagSearch;
  setSearch: (next: Partial<KbRagSearch>) => void;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const ask = useKbAsk();
  const { data: modelData } = useKbModels();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, ask.isPending]);

  const models = modelData?.models ?? [];
  const activeModel = modelId ?? modelData?.default_id ?? models[0]?.id;

  const send = () => {
    const content = draft.trim();
    if (!content || ask.isPending) return;
    const history: ChatMessage[] = [
      ...turns.map((t) => ({ role: t.role, content: t.content })),
      { role: "user" as const, content },
    ];
    setTurns((prev) => [...prev, { role: "user", content }]);
    setDraft("");
    ask.mutate(
      {
        messages: history,
        category: search.cat,
        model_id: activeModel,
        context_chunks: 6,
      },
      {
        onSuccess: (res) =>
          setTurns((prev) => [
            ...prev,
            {
              role: "assistant",
              content: res.answer,
              citations: res.citations,
              model: res.model,
            },
          ]),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <CategorySelect
          value={search.cat}
          onChange={(cat) => setSearch({ cat })}
          disabled={ask.isPending}
        />
        <Select
          value={activeModel}
          onValueChange={setModelId}
          disabled={ask.isPending}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Model" />
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {turns.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setTurns([])}>
            Clear conversation
          </Button>
        ) : null}
      </div>

      {turns.length === 0 && !ask.isPending ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Ask a how-to question the way a customer or support rep would. The
            answer is grounded in articles retrieved from the knowledge base and
            cites every one it used — check the sources rather than trust the
            summary.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            A question the knowledge base cannot answer is a gap. Note it for
            the Release Impact tab&rsquo;s gap list, or write the article.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-4">
        {turns.map((turn, i) => (
          <div
            key={i}
            className={cn(
              "flex",
              turn.role === "user" ? "justify-end" : "justify-start",
            )}
          >
            <div
              className={cn("max-w-3xl", turn.role === "user" && "max-w-xl")}
            >
              {turn.role === "user" ? (
                <div className="rounded-lg bg-brand/10 px-4 py-2 text-sm">
                  {turn.content}
                </div>
              ) : (
                <Card>
                  <CardContent className="pt-4">
                    <MarkdownView source={turn.content} />
                    {turn.citations && turn.citations.length > 0 ? (
                      <div className="mt-4 border-t pt-3">
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                          Articles retrieved for this answer
                        </p>
                        <div className="flex flex-col gap-1.5">
                          {turn.citations.map((c, ci) => (
                            <ArticleHit
                              key={`${c.document_id}-${ci}`}
                              hit={c}
                              index={ci + 1}
                              onOpenArticle={(id) =>
                                setSearch({ tab: "articles", article: id })
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {turn.model ? (
                      <p className="mt-3 text-xs text-muted-foreground">
                        {turn.model}
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ))}

        {ask.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Retrieving articles and composing an answer…
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {ask.error ? (
        <Alert variant="danger">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t answer</AlertTitle>
          <AlertDescription>
            {ask.error instanceof Error ? ask.error.message : String(ask.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="How do I set up a review request after a visit?"
          rows={2}
          className="flex-1 resize-none"
          disabled={ask.isPending}
        />
        <Button onClick={send} disabled={ask.isPending || !draft.trim()}>
          <CornerDownLeft className="mr-2 h-4 w-4" />
          Ask
        </Button>
      </div>
    </div>
  );
}
